<template>
  <div
    class="gf-text-editor"
    v-click-outside="handleClickOutside"
  >
    <div class="gf-text-editor__editor">
      <div v-if="editorVariant === 'textarea'">
        <label
          class="w-100"
          :class="maxLength ? 'gf-counted-input' : ''"
          for="repoName"
          :data-current-length="(textAreaModel?.length || 0) + ' / ' + maxLength"
        >
          <textarea
            ref="gfTextarea"
            class="gf-text-editor__textarea"
            v-model="textAreaModel"
            :class="{ 'is-invalid': hasError }"
            :name="editorName"
            :disabled="readOnly"
            :placeholder="placeholder"
            :maxlength="maxLength"
            :autofocus="autofocus"
            @input="onTextareaInput"
          />
        </label>
      </div>
      <div v-else-if="editorVariant === 'editor' && isEditorLoaded">
        <component
          :is="asyncComponentHandler"
          ref="gfEditor"
          :data="data"
          :data-type="dataType"
          :saved-data-type="savedDataType"
          :read-only="readOnly"
          :placeholder="placeholder"
          :clean-after-submit="cleanAfterSubmit"
          :has-error="hasError"
          :use-hidden-textarea="useHiddenTextarea"
          :hidden-textarea-name="hiddenTextareaName"
          :autofocus="autofocus"
          :allow-images="allowImages"
          @submit="submit"
        />
      </div>
    </div>
    <div
      v-if="hasError"
      class="gf-text-editor__errors"
    >
      {{ errorText }}
    </div>
    <div
      v-if="hasActionButtons"
      class="gf-text-editor__actions"
    >
      <button
        v-if="hasCloseButton"
        :class="closeButtonClass"
        @click="close"
      >
        {{ closeButtonText }}
      </button>
      <button
        :class="submitButtonClass"
        @click="submit"
      >
        {{ submitButtonText }}
      </button>
    </div>
  </div>
</template>

<script>
import { clickOutsideDirective } from '@/directives.js';
import GfCustomCollapse from './GfCustomCollapse.vue';

export default {
  name: 'GfCustomTextEditor',
  components: { GfCustomCollapse },
  directives: {
    'click-outside': clickOutsideDirective()
  },
  props: {
    // общие
    editorVariant: {
      type: String,
      default: 'textarea'
    },
    data: {
      default: null
    },
    dataType: {
      type: String,
      default: 'text'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: 'Напишите текст'
    },
    isOpened: {
      type: Boolean,
      default: true
    },
    openButtonDataName: {
      type: String,
      default: 'openGfEditor'
    },
    allowImages: {
      type: Boolean,
      default: true
    },

    // textarea
    editorName: {
      type: String,
      default: 'description'
    },
    maxLength: {
      type: Number
    },
    value: {
      type: String
    },

    // editor
    savedDataType: {
      type: String,
      default: 'markdown'
    },
    cleanAfterSubmit: {
      type: Boolean,
      default: true
    },
    useHiddenTextarea: {
      type: Boolean,
      default: false
    },
    hiddenTextareaName: {
      type: String,
      default: 'description'
    },

    // кнопки
    hasActionButtons: {
      type: Boolean,
      default: true
    },
    hasCloseButton: {
      type: Boolean,
      default: false
    },
    submitButtonText: {
      type: String,
      default: 'Сохранить'
    },
    submitButtonClass: {
      type: String,
      default: 'btn btn-success btn-sm'
    },
    closeButtonText: {
      type: String,
      default: 'Отмена'
    },
    closeButtonClass: {
      type: String,
      default: 'btn btn-danger btn-sm mr-2'
    },

    // вывод ошибок
    hasError: {},
    errorText: {
      type: String,
      default: null
    },

    // Тогглер для директивы
    closeOnUnfocus: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isEditorLoaded: false,
      asyncComponentHandler: null,
      openEditorButton: null,
      isHided: false,
      textAreaModel: this.value
    };
  },
  watch: {
    value(newVal) {
      if (newVal !== undefined) {
        this.textAreaModel = newVal;
      }
    }
  },
  mounted() {
    if (this.isOpened) {
      this.showEditor();
    } else {
      this.openEditorButton = document.querySelector(`[data-editor="${this.openButtonDataName}"]`);
      if (this.openEditorButton) this.openEditorButton.addEventListener('click', this.buttonClick);
    }
  },
  beforeDestroy() {
    if (this.openEditorButton) this.openEditorButton.removeEventListener('click', this.buttonClick);
  },
  methods: {
    buttonClick() {
      this.showEditor();
    },
    showEditor() {
      if (this.editorVariant === 'editor') {
        this.loadEditor();
      }
    },
    loadEditor() {
      if (!this.isEditorLoaded) {
        import('@/shared-modules/shared-components/shared/GfCustomEditor.vue')
          .then((component) => {
            this.asyncComponentHandler = component.default;
            this.isEditorLoaded = true;
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    onKeydown(e) {
      if (e.keyCode === 13 && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.submit();
      }
    },
    onTextareaInput(e) {
      this.$emit('input', e.target.value);
    },
    // eslint-disable-next-line consistent-return
    async getData() {
      if (this.editorVariant === 'textarea') {
        return this.$refs.gfTextarea.value;
      }
      if (this.editorVariant === 'editor') {
        // eslint-disable-next-line no-return-await
        return await this.$refs.gfEditor.save().then((res) => res);
      }
    },
    close() {
      this.$emit('close');
    },
    hide() {
      this.$emit('hide');
    },
    submit() {
      this.$emit('submit', this.getData());
    },
    // Handler для директивы
    handleClickOutside() {
      if (!this.closeOnUnfocus || !this.$refs.gfEditor) return;
      // this.close();
      this.hide();
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-text-editor';

#{$this} {
  #{$this}__editor {
    & *.is-invalid {
      border-color: #dc3545 !important;
      padding-right: calc(1.5em + 0.75rem);
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right calc(0.375em + 0.1875rem) center;
      background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }
  }
  #{$this}__textarea {
    width: 100%;
    height: 150px;
    padding: 15px 20px;
    box-sizing: border-box;
    color: var(--text);
    background-color: var(--white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    caret-color: var(--text);
    resize: none;
    transition: border-color 0.15s ease-in-out;

    &:focus,
    &:active {
      border-color: var(--primary);
    }
  }
  #{$this}__errors {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 12px;
    color: #dc3545;
  }
  #{$this}__actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 15px;
  }
}
</style>
